/**
 * Client-side safety net for an enrichment run.
 *
 * The progress UI learns that a run finished from ONE place: the SSE stream
 * delivering `complete` or `error`. If that stream never delivers -- a proxy
 * that buffers or drops it, a worker that commits a state the generator does
 * not treat as terminal -- the spinner has no other way out and runs forever.
 *
 * The server already runs belt-and-braces internally (Redis pub/sub *plus* a
 * 5s DB poll). This is the same idea on the client: poll the contact's status
 * alongside the stream and let whichever reaches a terminal state first win.
 *
 * SSE stays the fast path -- it carries the live step trail and normally wins
 * the race -- so this changes nothing on a healthy connection beyond one small
 * GET every few seconds while a run is genuinely in progress.
 */

export interface EnrichmentSnapshot {
  status: string
  enrichment_data?: unknown
  enrichment_results?: Record<string, unknown>
}

/** Statuses the backend commits when a run is over, either way. */
export const TERMINAL_ENRICHMENT_STATUSES = ['completed', 'failed'] as const

export function isTerminalEnrichmentStatus(status: string | null | undefined): boolean {
  if (!status) return false
  return (TERMINAL_ENRICHMENT_STATUSES as readonly string[]).includes(status)
}

export interface PollEnrichmentOptions {
  /** Reads the contact's current enrichment state. Rejections are tolerated. */
  fetchStatus: () => Promise<EnrichmentSnapshot | null | undefined>
  /** Called at most once, with the first terminal snapshot seen. */
  onTerminal: (snapshot: EnrichmentSnapshot) => void
  /** Gap between attempts. Default 5s, matching the server's own DB poll. */
  intervalMs?: number
  /**
   * Give up after this long and stop polling silently. Native enrichment's own
   * budget is 900s (PROVIDER_TIMEOUTS["native"]), so the default leaves ample
   * room for a slow run while still guaranteeing the timer cannot outlive the
   * page. Reaching it does NOT resolve the UI -- it just stops asking.
   */
  maxDurationMs?: number
  /** Called when an attempt throws. Polling continues regardless. */
  onError?: (error: unknown) => void
}

/**
 * Poll until the run reaches a terminal state.
 *
 * Returns a `stop()` that is safe to call any number of times, including from
 * a React cleanup after the component has unmounted.
 *
 * Uses a self-rescheduling timeout rather than setInterval: a slow or hung
 * request must not let attempts pile up on top of each other.
 */
export function pollEnrichmentUntilTerminal(options: PollEnrichmentOptions): () => void {
  const {
    fetchStatus,
    onTerminal,
    intervalMs = 5000,
    maxDurationMs = 20 * 60 * 1000,
    onError,
  } = options

  let stopped = false
  let settled = false
  let timer: ReturnType<typeof setTimeout> | null = null
  const startedAt = Date.now()

  const stop = () => {
    stopped = true
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const attempt = async () => {
    timer = null
    if (stopped) return

    try {
      const snapshot = await fetchStatus()
      // The response can land after a stop() -- an in-flight request is not
      // cancelled by clearTimeout, so re-check before doing anything with it.
      if (stopped || settled) return
      if (snapshot && isTerminalEnrichmentStatus(snapshot.status)) {
        settled = true
        stop()
        onTerminal(snapshot)
        return
      }
    } catch (error) {
      if (stopped) return
      // A failed poll is not a failed run. Report it and keep asking: the
      // whole point of this loop is to survive a flaky path to the server.
      onError?.(error)
    }

    schedule()
  }

  const schedule = () => {
    if (stopped || settled) return
    if (Date.now() - startedAt >= maxDurationMs) {
      stop()
      return
    }
    timer = setTimeout(attempt, intervalMs)
  }

  // First attempt is one interval in, not immediate: the stream endpoint
  // already checks for an - already-finished run before it subscribes, so an
  // immediate poll would duplicate that on every single run.
  schedule()

  return stop
}
